import {
  isBefore as isBeforeRemote,
  isAfter,
  formatDistance,
  isSameMinute,
  isToday,
  startOfDay,
  differenceInMilliseconds,
  differenceInDays,
  differenceInYears,
  format,
  parseISO,
  isValid,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { formatToTimeZone, parseFromTimeZone } from 'date-fns-timezone';

const timeFormat = 'h:mm a';
const dateFormat = 'P';
const dateFormatTZ = 'MM/DD/YYYY';
const timeFormatTZ = 'HH:mm z';

const cfg = { locale: enUS };

export const INVALID_DATE = 'INVALID_DATE';

// This is used in the Settings.js file as a sort of "infinite" date
// Note: This is a constant that is used to set the global do not disturb to an infinite duration
// Ensure changes to this value are also made in the mobile app
export const INFINITE_DND_CONSTANT = '2300-12-31T06:00:00.000Z';

// This value is used to specify what the largest safe 32-bit integer is for
// browser based methods like setInterval and setTimeout
export const MAX_SAFE_INTERVAL_MS = 2_147_483_647; // about 24.8 days

export const parse = parseISO;

export const calculateAgeFromDOB = (dob: string): number => differenceInYears(new Date(), new Date(dob));

export const formatTime = (date: Date): string => {
  if (date === null) {
    return 'N/A';
  }
  if (isValid(date)) {
    return format(date, timeFormat, cfg);
  }
  return 'Invalid Date';
};

export const formatDate = (date: string | Date, utc: boolean = false): string | undefined => {
  const inputDate = typeof date === 'string' ? parse(date) : date;

  if (isValid(inputDate)) {
    if (utc) {
      return format(inputDate, dateFormat);
    }
    return format(inputDate, dateFormat, cfg);
  }

  return undefined;
};

// Note: format will translate the incoming UTC timestamp to device time zone
export const formatDateTime = (ts: string, separator: string = ' '): string => {
  if (ts === null || ts === '') {
    return 'N/A';
  }
  const date = parse(ts);
  if (isValid(date)) {
    const tpl = isToday(date) ? timeFormat : `${dateFormat}${separator}${timeFormat}`;

    return format(date, tpl, cfg);
  }
  return 'Invalid Date';
};

// Note: format will translate the incoming UTC timestamp to device time zone
export const formatFullDateTime = (ts: string, separator: string = ' '): string => {
  if (ts === null || ts === '') {
    return 'date is unavailable';
  }
  const date = parse(ts);
  if (isValid(date)) {
    return format(date, `${dateFormat}${separator}${timeFormat}`, cfg);
  }

  return 'date is unavailable';
};

// Note: format will translate the incoming UTC timestamp to device time zone
export const formatFullDateTimeWithTZ = (ts: string): string => {
  if (ts === null || ts === '') {
    return 'date is unavailable';
  }
  const date = parse(ts);
  if (isValid(date)) {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const tpl = `${dateFormatTZ} ${timeFormatTZ}`;
    return formatToTimeZone(date, tpl, { timeZone });
  }
  return 'date is unavailable';
};

export const formatDateTimeToTZ = (ts: string, tz: string, dst: boolean): string => {
  const timezone = getCanonicalTimeZone(tz, dst);

  if (ts === '' || timezone === '') {
    return '';
  }

  const date = parseFromTimeZone(ts, { timeZone: getCanonicalTimeZone(tz, dst) });

  if (isValid(date)) {
    return date.toISOString();
  }

  return '';
};

function getCanonicalTimeZone(tz: string, dstObserved: boolean) {
  switch (tz) {
    case 'Eastern':
      return dstObserved ? 'America/New_York' : 'America/Jamaica'; // just using Jamaica because DST is not observed
    case 'Central':
      return dstObserved ? 'America/Chicago' : 'America/Belize'; // just using Belize because DST is not observed
    case 'Mountain':
      return dstObserved ? 'America/Denver' : 'America/Phoenix';
    case 'Pacific':
      return 'America/Los_Angeles'; // Looks like all of pacific time observes dst
    default:
      return '';
  }
}

export const parseDate = (ts: string): Date | null => {
  if (ts === null || ts === '') {
    return null;
  }

  const date = parse(ts);
  if (isValid(date)) {
    return date;
  }

  return null;
};

export const isEqualMinute = (ts1: string, ts2: string) => {
  const date1 = parse(ts1);
  const date2 = parse(ts2);

  return isSameMinute(date1, date2);
};

export const distanceToNow = (ts: string): string => formatDistance(parse(ts), Date.now());

export const isoNow = (): string => format(Date.now(), timeFormat, cfg);
export const isoNowUTC = (): string => new Date(Date.now()).toISOString();

// Note: Offset is in milliseconds
export const offsetToNow = (offset: number): Date => new Date(Date.now() + offset);
export const isoOffset = (offset: number) => format(offsetToNow(offset), timeFormat, cfg);

// naive implementation of formatRelative coming in date-fns@2
//  see: https://date-fns.org/v2.0.0-alpha.10/docs/formatRelative
export const formatRelative = (ts1: Date): string => {
  const diffDays = differenceInDays(startOfDay(ts1), startOfDay(new Date()));

  if (Math.abs(diffDays) <= 6) {
    if (diffDays === 0) {
      return `Today at ${formatTime(ts1)}`;
    }
    if (diffDays === 1) {
      return `Tomorrow at ${formatTime(ts1)}`;
    }
    if (diffDays === -1) {
      return `Yesterday at ${formatTime(ts1)}`;
    }

    const dayName = format(ts1, 'EEEE', cfg);

    return `${diffDays < 0 ? 'Last ' : ''}${dayName} at ${formatTime(ts1)}`;
  }

  return format(ts1, `${dateFormat} ${timeFormat}`, cfg);
};

// Return a date string in the form of 'yyyy-mm-dd' based on the given date
export const getLocalISODateString = (ts: Date) => {
  const tsLocal = new Date(ts.getTime());
  tsLocal.setMinutes(ts.getMinutes() - ts.getTimezoneOffset());
  return tsLocal.toISOString().slice(0, -14);
};

// Return a datetime string in the format 'yyyy-mm-ddTHH:mm'
export const getLocalISODateTimeString = (ts: Date) => {
  const tsLocal = new Date(ts.getTime());
  tsLocal.setMinutes(ts.getMinutes() - ts.getTimezoneOffset());
  return tsLocal.toISOString().slice(0, -8);
};

export const isFutureDatetime = (ts: string) => isAfter(parse(ts), new Date());

export const isBefore = (ts1: string, ts2: string) => isBeforeRemote(parse(ts1), parse(ts2));

export const millisecondsUntil = (ts: string) => differenceInMilliseconds(parse(ts), new Date());

export const getDateOnlyDisplay = (dateStr: string): string => {
  // This method assumes that the incoming date string is an ISO formatted date string,
  // it will attempt to parse it and return the date only display string or a suitable fallback
  if (typeof dateStr !== 'string') return '';

  // Try ISO-like format YYYY-MM-DD, lopping off any time component
  const parts = dateStr.slice(0, 10).split('-');
  if (parts.length === 3) {
    // Safety checks, make sure we have valid numbers
    const [year, month, day] = parts.map(Number);

    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
      const d = new Date(year, month - 1, day);
      if (isValid(d)) return format(d, dateFormat, cfg);
    }
  }

  // Fallback: try to parse with Date constructor
  const fallback = new Date(dateStr);
  return isValid(fallback) ? format(fallback, dateFormat, cfg) : '';
};
